import { zodResolver } from "@hookform/resolvers/zod";
import { useIsFirstRender, usePrevious } from "@uidotdev/usehooks";
import { CornerDownLeft, Loader } from "lucide-react";
import { type KeyboardEvent, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { CostCenterAutocompleteInput } from "@/components/shared/form/cost-center-autocomplete-input";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import { TextInput } from "@/components/shared/form/text-input";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { InvoiceModes } from "@/features/invoice-standard-review/accounting/splitInvoiceForm/SplitInvoiceForm";
import { EVENT_NAMES } from "@/lib/events/names";
import { useTenant } from "@/providers/session";
import { useGetAllAccounts } from "@/queries/accounts/get-all";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

import { AccountAutocompleteInput } from "@/components/shared/form/account-autocomplete-input";
import { TaxCodeAutocompleteInput } from "@/components/shared/form/tax-code-autocomplete-input";
import { FormNavigation } from "../form/shared/form-navigation";

const defaultValues = {
  bookingText: null,
  accountId: undefined,
  taxCodeId: undefined,
  costCenterId: undefined,
  amount: undefined,
};

const addLineItemFormSchema = z.object({
  bookingText: z.string().max(255).nullable(),
  accountId: z.string().max(255),
  taxCodeId: z.string().max(255).nullable(),
  costCenterId: z.string().max(255).nullable().optional(),
  amount: z.number({ message: "Umsatz darf nicht leer sein" }),
});

export type AddLineItemFormType = z.infer<typeof addLineItemFormSchema>;

type Props = {
  onSubmit?: (data: AddLineItemFormType) => Promise<void>;
  invoiceMode: InvoiceModes;
};

export const AddBookingForm = ({ onSubmit, invoiceMode }: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();
  const { accounts } = useGetAllAccounts();
  const { taxCodes } = useGetAllTaxCodes();
  const { costCenters } = useGetAllCostCenters();
  const previousInvoiceMode = usePrevious(invoiceMode);
  const isFirstRender = useIsFirstRender();
  const { hasCostCenters } = useTenant();

  const form = useForm<AddLineItemFormType>({
    resolver: zodResolver(addLineItemFormSchema),
    defaultValues,
  });

  const handleFormFocus = useCallback(() => {
    const firstInput = formRef.current?.querySelector("input");
    if (firstInput) {
      firstInput.focus();
    }
  }, []);

  useEffect(() => {
    if (
      !isFirstRender &&
      previousInvoiceMode &&
      previousInvoiceMode !== invoiceMode
    ) {
      handleFormFocus();
    }
  }, [invoiceMode, previousInvoiceMode, handleFormFocus, isFirstRender]);

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      const resetValues = {
        accountId: defaultValues.accountId,
        taxCodeId: defaultValues.taxCodeId,
        amount: defaultValues.amount,
        bookingText: form.getValues("bookingText"),
      };
      form.reset(resetValues);
      handleFormFocus();
    }
  }, [
    form.formState.isSubmitSuccessful,
    form.reset,
    handleFormFocus,
    form.getValues,
  ]);

  const handleSubmit = async ({
    bookingText,
    accountId,
    taxCodeId,
    costCenterId,
    amount,
  }: AddLineItemFormType) => {
    await onSubmit?.({
      bookingText,
      accountId,
      taxCodeId,
      costCenterId,
      amount,
    });
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      form.handleSubmit(handleSubmit)();
      return;
    }

    onKeyDown(e);
  };

  const handleSetAmount = useCallback((event: any) => {
    const currentAmount = form.getValues("amount");
    let amount = event.detail.value;
    if (
      currentAmount === null ||
      currentAmount === 0 ||
      currentAmount === undefined
    )
      amount *= 100;

    form.setValue("amount", amount);
    handleFormFocus();
  }, []);

  useEffect(() => {
    window.addEventListener(EVENT_NAMES.ADD_LINE_ITEM, handleFormFocus);
    window.addEventListener(EVENT_NAMES.PROPAGATE_AMOUNT, handleSetAmount);

    return () => {
      window.removeEventListener(EVENT_NAMES.ADD_LINE_ITEM, handleFormFocus);
      window.removeEventListener(EVENT_NAMES.PROPAGATE_AMOUNT, handleSetAmount);
    };
  }, []);

  useHotkeys(["shift+equal", "+", "BracketRight", "NumpadAdd"], () => {
    setTimeout(() => {
      handleFormFocus();
    }, 100);
  });

  useHotkeys("mod+backspace", () => {
    form.reset(defaultValues);
  });

  return (
    <Form {...form}>
      <form
        ref={formRef}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex items-center gap-2 w-full"
      >
        <FormNavigation className="flex-1" submitOnLastInput>
          {({ onKeyDown }) => {
            return (
              <div className="flex flex-1 items-start gap-2 w-full">
                <FormNumberInput
                  name="amount"
                  placeholder={`Umsatz ${invoiceMode === InvoiceModes.Brutto ? "(Brutto)" : "(Netto)"}`}
                  onKeyDown={onKeyDown}
                />
                <TaxCodeAutocompleteInput
                  name="taxCodeId"
                  placeholder="BU"
                  options={taxCodes ?? []}
                  onKeyDown={onKeyDown}
                  showEmptyLabel
                  side="top"
                />
                <AccountAutocompleteInput
                  name="accountId"
                  placeholder="Konto"
                  options={accounts ?? []}
                  showEmptyLabel
                  onKeyDown={onKeyDown}
                  side="top"
                />
                {hasCostCenters && (
                  <CostCenterAutocompleteInput
                    name="costCenterId"
                    placeholder="Kostenstelle"
                    options={costCenters ?? []}
                    showEmptyLabel
                    onKeyDown={onKeyDown}
                    side="top"
                  />
                )}
                <TextInput
                  name="bookingText"
                  placeholder="Buchungstext"
                  onKeyDown={onKeyDown}
                  selectTextOnFocus
                  autoComplete="off"
                />
                <Button
                  type="submit"
                  onKeyDown={(e) => handleKeyDown(e, onKeyDown)}
                  disabled={form.formState.isSubmitting}
                >
                  {t("save")}
                  {form.formState.isSubmitting ? (
                    <Loader className="ml-1 animate-spin" size={16} />
                  ) : (
                    <CornerDownLeft className="ml-1" size={16} />
                  )}
                </Button>
              </div>
            );
          }}
        </FormNavigation>
      </form>
    </Form>
  );
};
